import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { useHistory, useParams } from 'react-router-dom';
import { confirmPassword, getInviteInfo } from '../../../store/services/actions';
import companyServicesInfoSelector from '../../../store/services/selectors';
import styles from '../Login.module.scss';
import BackgroundWrapper from '../BackgroundWrapper';
import Card from '../../Card';
import Button from '../../Core/Button/Button';
import Input from '../../Core/Input/Input';
import passwordValidator from '../../Helpers/passwordValidator';
import LockLoginIcon from '../../Icons/LockLoginIcon';
import Logo from '../../Logo';
import classes from './InvitePage.module.scss';

import appStoreImg from '../../../assets/app_store_btn.png'
import googlePlayImg from '../../../assets/google_play_btn.png'

const InvitePage = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    email,
    security,
    company: { companyName = '', lang = 'en' },
    loading,
    admin,
  } = useSelector(companyServicesInfoSelector);

  localStorage.setItem('i18nextLng', lang);

  useLayoutEffect(() => {
    dispatch(getInviteInfo(token));
  }, [dispatch, token]);

  const {
    min_password_length: minLength = 1,
    special_chars: specialChars = false,
    numbers = false,
    uppercase = false,
  } = security;

  const [values, setValues] = useState({
    password: '',
    repeatPassword: '',
  });
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('update_password') // update_password
  const [matchError, setMatchError] = useState(false);

  const handleInput = (e) => {
    const fieldId = e.target.name;
    const text = e.target.value;
    setMatchError(false);

    setValues((prevState) => ({
      ...prevState,
      [fieldId]: text,
    }));
  };

  const validateOnBlur = () => {
    if (values.password && values.repeatPassword) {
      if (values.password !== values.repeatPassword) {
        setMatchError(true);
      } else {
        setMatchError(false);
      }
    }
  };

  useEffect(() => {
    if (values.password) {
      const { password: err } = passwordValidator({
        password: values.password, minLength, numbers, specialChars, uppercase,
      });
      if (!isEmpty(err)) {
        setError(err);
      } else {
        setError(null);
      }
    }
  }, [minLength, numbers, specialChars, uppercase, values.password]);

  const onSubmitHandler = () => {
    if (!error && !matchError && values.password && values.repeatPassword) {
      dispatch(confirmPassword({
        token,
        password_confirmation: values.repeatPassword,
        password: values.password,
        email,
      })).then(({ data }) => {
        // localStorage.setItem('token', data.access_token);
        setMode('completed')
      }).catch((e) => console.error(e));
    }
  };

  return (
    <BackgroundWrapper className={classes.root}>
      <Card className={classes.card}>
        <Logo />
        {
          mode === 'update_password'
            ? <>
                <div className={classes.description}>
                  <p>
                    {
                      admin
                        ? t(`You are the main admin user of the {{company}} organization account.`, { company: companyName })
                        : t(`You have been invited to the {{company}} organization account as a company employee.`, { company: companyName })
                    }
                  </p>
                  <p>
                    {`${t('Your registered e-mail')}:`}
                  </p>
                  <p className={classes.black}>{email}</p>
                  <p>{t('Please create your password')}</p>
                </div>
                <form>
                  <div className={classes.formItem}>
                    <Input
                      name='password'
                      type='password'
                      value={values.password}
                      fullWidth
                      underlined
                      iconLeft
                      placeholder={t('Create password')}
                      icon={<LockLoginIcon aria-hidden />}
                      onChange={handleInput}
                      onBlur={validateOnBlur}
                    />
                  </div>
                  <div className={classes.formItem}>
                    <Input
                      name='repeatPassword'
                      type='password'
                      fullWidth
                      value={values.repeatPassword}
                      underlined
                      iconLeft
                      placeholder={t('Re-enter password')}
                      icon={<LockLoginIcon aria-hidden />}
                      onChange={handleInput}
                      onBlur={validateOnBlur}
                    />
                  </div>
                  <div className={styles.errorBlock}>
                    {
                      matchError && <p>{t('Passwords do not match')}</p>
                    }
                  </div>
                  <div className={classes.buttonBlock}>
                    <Button
                      onClick={onSubmitHandler}
                      size='large'
                      disabled={matchError || !values.password || !values.repeatPassword || error || loading}
                    >
                      {t('Login')}
                    </Button>
                  </div>
                  {
                    error && (
                      <p className={classes.error}>{error.message}</p>
                    )
                  }
                </form>
              </>
            : <div className={classes.description}>
                <p>{t('Your password has been changed, as requested.')}</p>
                <p>{t('If you did not request a password change, we will help ensure the security of your account. Please contact us by email at ')}<a className={classes.link} href="mailto:support@grownu.com?subject=Support Grownu">support@grownu.com.</a></p>
                <p>{t('You can now log in to the service at: ')}<br /><a className={classes.link} href="/login">{window.location.protocol}//{window.location.host}</a></p>
                <p>{t('To access the mobile application, download one of the following apps based on your device’s operating system:')}</p>
                <div className={classes.buttonsContainer}>
                  <a className={cn(classes.appButton, classes.apple)} href="https://apps.apple.com/lt/app/grownu/id1613354708" target="_blank" rel="noreferrer">
                    <img src={appStoreImg} />
                  </a>
                  <a className={cn(classes.appButton, classes.google)} href="https://play.google.com/store/apps/details?id=com.app.grownu" target="_blank" rel="noreferrer">
                    <img src={googlePlayImg} />
                  </a>
                </div>
                <Button
                  onClick={() => history.push('/login')}
                  size='large'
                >
                  {t('Login')}
                </Button>
              </div>
        }
        
      </Card>
    </BackgroundWrapper>
  );
};

export default InvitePage;
